import { db, safeDbOperation } from '../lib/db';
import { Recipe } from '../types/recipe';
import { RecipeInput } from '../schemas/recipe';

export const addRecipe = async (recipe: RecipeInput): Promise<Recipe> => {
  console.log('--- Recipe Service: Add Recipe Started ---', {
    timestamp: new Date().toISOString(),
    recipeData: recipe
  });

  return safeDbOperation(async () => {
    const id = crypto.randomUUID();
    const newRecipe: Recipe = {
      id,
      ...recipe,
      dietaryTags: Array.isArray(recipe.dietaryTags) ? recipe.dietaryTags : [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    console.log('New recipe prepared:', {
      recipeId: newRecipe.id,
      title: newRecipe.title,
      category: newRecipe.category
    });

    await db.recipes.add(newRecipe);
    
    console.log('Recipe successfully added to database', {
      status: 'success',
      recipeId: newRecipe.id,
      timestamp: new Date().toISOString()
    });

    return newRecipe;
  });
};

export const updateRecipe = async (
  id: string,
  recipe: Partial<RecipeInput>
): Promise<Recipe> => {
  return safeDbOperation(async () => {
    const existingRecipe = await db.recipes.get(id);
    if (!existingRecipe) throw new Error('Recipe not found');

    const updatedRecipe: Recipe = {
      ...existingRecipe,
      ...recipe,
      updatedAt: new Date(),
    };

    await db.recipes.put(updatedRecipe);
    return updatedRecipe;
  });
};

export const deleteRecipe = async (id: string): Promise<void> => {
  return safeDbOperation(async () => {
    const recipe = await db.recipes.get(id);
    if (!recipe) throw new Error('Recipe not found');
    
    await db.recipes.delete(id);
    
    // Return a promise that resolves after the deletion is complete
    return Promise.resolve();
  });
};

export const getRecipe = async (id: string): Promise<Recipe> => {
  return safeDbOperation(async () => {
    const recipe = await db.recipes.get(id);
    if (!recipe) throw new Error('Recipe not found');
    return recipe;
  });
};

export const getRecipes = async (
  category?: string,
  searchTerm?: string,
  selectedTag?: string
): Promise<Recipe[]> => {
  return safeDbOperation(async () => {
    let collection = db.recipes.orderBy('createdAt').reverse();
    
    if (category) {
      collection = collection.filter(recipe => recipe.category === category);
    }

    let recipes = await collection.toArray();

    // Filter by selected tag first if it exists
    if (selectedTag) {
      recipes = recipes.filter(recipe => 
        recipe.dietaryTags.some(tag => 
          tag.toLowerCase() === selectedTag.toLowerCase()
        )
      );
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const searchTerms = searchLower.split(/[,\s]+/).filter(Boolean);
      
      recipes = recipes.filter(
        (recipe) => {
          const titleMatch = recipe.title.toLowerCase().includes(searchLower);
          const ingredientMatch = recipe.ingredients.some(ing => 
            searchTerms.some(term => ing.name.toLowerCase().includes(term))
          );
          const tagMatch = recipe.dietaryTags.some(tag => 
            searchTerms.some(term => tag.toLowerCase() === term)
          );
          
          return titleMatch || ingredientMatch || tagMatch;
        }
      );
    }

    return recipes;
  });
};